import AccountCombobox from "@app/components/account-combobox";
import type { AccountOption } from "@app/components/account-combobox";
import StakeholderCombobox from "@app/components/stakeholder-combobox";
import type { StakeholderOption } from "@app/components/stakeholder-combobox";
import { BulkAction } from "@app/components/txn-table-panel";
import { useToast } from "@app/components/toast";
import { environment } from "@app/relay/relay-environment";
import { useState } from "react";
import type { ReactNode } from "react";
import { commitMutation, graphql } from "react-relay";

import CreateRuleDialog from "@app/pages/transactions/components/create-rule-dialog";
import RuleConflictDialog from "@app/components/txn-table-panel/rule-conflict-dialog";
import type { ConflictingRule } from "@app/components/txn-table-panel/rule-conflict-dialog";

import type { useTxnBulkActions_AttributeTxnsMutation } from "@app/queries/useTxnBulkActions_AttributeTxnsMutation.graphql";
import type {
  useTxnBulkActions_ClassifyTxnsMutation,
  TxnClassificationEnum,
} from "@app/queries/useTxnBulkActions_ClassifyTxnsMutation.graphql";
import type { useTxnBulkActions_MarkTxnsInternalMutation } from "@app/queries/useTxnBulkActions_MarkTxnsInternalMutation.graphql";
import type { useTxnBulkActions_AssignTxnNatureMutation } from "@app/queries/useTxnBulkActions_AssignTxnNatureMutation.graphql";
import type { useTxnBulkActions_SetTxnsNeedsReviewMutation } from "@app/queries/useTxnBulkActions_SetTxnsNeedsReviewMutation.graphql";

export type NatureOption = { readonly id: string; readonly name: string };

// The single registry of txn bulk actions, shared by every editable txn panel.
// A surface names the actions it wants in `enabled`; the hook supplies the handler,
// the BulkAction popover, and the option list. Replaces the per-panel copies of
// attribute / mark-internal / classify / nature / needs-review + the create-rule
// flow, and is the one place the canonical reclassify option set lives.
export type TxnBulkActionKey =
  | "setCounterparty"
  | "createRule"
  | "markInternal"
  | "reclassify"
  | "tagNature"
  | "sendToNeedsReview";

// The canonical reclassify set (unifies cash-position's TAX_PAYMENT and pnl's SALARY
// into one list, so every surface offers the same overrides). Clear sends the txn
// back to the operating corpus.
export const RECLASSIFY_OPTIONS: { value: TxnClassificationEnum | null; label: string }[] = [
  { value: "LOAN_PROCEEDS", label: "Loan proceeds (debt in)" },
  { value: "LOAN_REPAYMENT", label: "Loan repayment (debt out)" },
  { value: "DIVIDEND", label: "Dividend (owner draw)" },
  { value: "SALARY", label: "Salary" },
  { value: "TAX_PAYMENT", label: "Tax payment" },
  { value: "TAX_REFUND", label: "Tax payback" },
  { value: "FRAUDULENT", label: "Fraudulent" },
  { value: null, label: "Clear (operating)" },
];

const attributeTxnsMutation = graphql`
  mutation useTxnBulkActions_AttributeTxnsMutation($input: AttributeTxnsInputObject!) {
    attributeTxns(input: $input) {
      stakeholder {
        id
        name
      }
      # The rules that would put these txns straight back where they came from.
      # Selected on every surface, so the offer to fix them is not a property of
      # which panel the correction happened to be made from.
      conflictingRules {
        id
        regex
        matchKind
        caseSensitive
        direction
        matchField
        isCaptureRule
        matchesCount
        stakeholder {
          id
          name
        }
      }
      errors {
        message
      }
    }
  }
`;

const markTxnsInternalMutation = graphql`
  mutation useTxnBulkActions_MarkTxnsInternalMutation($input: MarkTxnsInternalInputObject!) {
    markTxnsInternal(input: $input) {
      updatedCount
      errors {
        message
      }
    }
  }
`;

const classifyTxnsMutation = graphql`
  mutation useTxnBulkActions_ClassifyTxnsMutation($input: ClassifyTxnsInputObject!) {
    classifyTxns(input: $input) {
      updatedCount
      errors {
        message
      }
    }
  }
`;

const assignTxnNatureMutation = graphql`
  mutation useTxnBulkActions_AssignTxnNatureMutation($input: AssignTxnNatureInputObject!) {
    assignTxnNature(input: $input) {
      updatedCount
      errors {
        message
      }
    }
  }
`;

const setTxnsNeedsReviewMutation = graphql`
  mutation useTxnBulkActions_SetTxnsNeedsReviewMutation($input: SetTxnsNeedsReviewInputObject!) {
    setTxnsNeedsReview(input: $input) {
      updatedCount
      errors {
        message
      }
    }
  }
`;

type MinimalRow = {
  readonly txn: { readonly id: string };
  readonly description: string;
  // Optional: every txn panel selects it, but the registry only needs it to name
  // the counterparty a re-attribution moved AWAY from in the rule-conflict prompt.
  readonly counterparty?: { readonly name: string } | null;
};

// What the rule-conflict prompt needs to explain itself, captured at the moment of
// the attribution: `after` clears the selection before the prompt opens, so none of
// this can be re-derived from the rows afterwards.
type RuleConflict = {
  rules: readonly ConflictingRule[];
  txnIds: readonly string[];
  movedFromName: string;
  movedToName: string;
  movedToId: string;
};

// The distinct counterparty names the selected rows currently show — what the
// re-attribution is moving them AWAY from. Empty when the panel's rows carry no
// counterparty (a surface that hides the column).
function namesOf(rows: readonly MinimalRow[], txnIds: readonly string[]): string {
  const selected = new Set(txnIds);
  const names = new Set(
    rows
      .filter((row) => selected.has(row.txn.id))
      .map((row) => row.counterparty?.name)
      .filter((name): name is string => !!name)
  );
  return [...names].join(", ");
}

export function useTxnBulkActions({
  enabled,
  perspectiveId,
  onChanged,
  rows = [],
  stakeholderOptions = [],
  accountOptions = [],
  natureOptions = [],
}: {
  enabled: readonly TxnBulkActionKey[];
  perspectiveId?: string | null;
  onChanged: () => void;
  rows?: readonly MinimalRow[];
  stakeholderOptions?: readonly StakeholderOption[];
  accountOptions?: readonly AccountOption[];
  natureOptions?: readonly NatureOption[];
}): {
  busy: boolean;
  renderBulkActions: (selectedIds: string[], clearSelection: () => void) => ReactNode;
  dialogs: ReactNode;
} {
  const notify = useToast();
  const [busy, setBusy] = useState(false);
  const [ruleSeed, setRuleSeed] = useState<{ description: string; clearSelection: () => void } | null>(null);
  const [ruleConflict, setRuleConflict] = useState<RuleConflict | null>(null);

  const has = (key: TxnBulkActionKey) => enabled.includes(key);

  const after = (clearSelection: () => void, message: string) => {
    clearSelection();
    setBusy(false);
    notify({ message, kind: "success" });
    onChanged();
  };
  const fail = (message: string) => {
    setBusy(false);
    notify({ message, kind: "error" });
  };

  const attribute = (txnIds: string[], clearSelection: () => void, choice: { stakeholderId?: string; name?: string }) => {
    setBusy(true);
    // Read the outgoing counterparty off the rows BEFORE the write — the refetched
    // rows no longer carry the owner this correction is moving them away from.
    const movedFromName = namesOf(rows, txnIds);
    commitMutation<useTxnBulkActions_AttributeTxnsMutation>(environment, {
      mutation: attributeTxnsMutation,
      variables: { input: { txnIds, stakeholderId: choice.stakeholderId, name: choice.name, perspectiveId: perspectiveId ?? undefined } },
      onCompleted: (res) => {
        if (res.attributeTxns?.errors?.length) return fail(res.attributeTxns.errors[0].message);
        const conflicts = res.attributeTxns?.conflictingRules ?? [];
        const movedTo = res.attributeTxns?.stakeholder;
        after(clearSelection, `Set the counterparty on ${txnIds.length} transaction(s).`);
        // The write succeeded, but a standing rule would redo the mistake on the
        // next import. Ask about it now, on top of the refreshed panel.
        if (conflicts.length > 0 && movedTo) {
          setRuleConflict({
            rules: conflicts,
            txnIds,
            // The conflicting rule's own target names the counterparty when the
            // rows didn't carry one.
            movedFromName: movedFromName || conflicts[0].stakeholder?.name || "that counterparty",
            movedToName: movedTo.name,
            movedToId: movedTo.id,
          });
        }
      },
      onError: () => fail("Could not attribute — please try again."),
    });
  };

  const markInternal = (txnIds: string[], clearSelection: () => void, choice: { bankAccountId?: string; name?: string }) => {
    setBusy(true);
    commitMutation<useTxnBulkActions_MarkTxnsInternalMutation>(environment, {
      mutation: markTxnsInternalMutation,
      variables: { input: { txnIds, destinationBankAccountId: choice.bankAccountId, destinationName: choice.name } },
      onCompleted: (res) => {
        if (res.markTxnsInternal?.errors?.length) return fail(res.markTxnsInternal.errors[0].message);
        after(clearSelection, `Marked ${txnIds.length} as internal (removed from this flow).`);
      },
      onError: () => fail("Could not mark internal — please try again."),
    });
  };

  const reclassify = (txnIds: string[], clearSelection: () => void, classification: TxnClassificationEnum | null) => {
    setBusy(true);
    commitMutation<useTxnBulkActions_ClassifyTxnsMutation>(environment, {
      mutation: classifyTxnsMutation,
      variables: { input: { txnIds, classification } },
      onCompleted: (res) => {
        if (res.classifyTxns?.errors?.length) return fail(res.classifyTxns.errors[0].message);
        after(clearSelection, `Reclassified ${txnIds.length} transaction(s).`);
      },
      onError: () => fail("Could not reclassify — please try again."),
    });
  };

  const tagNature = (txnIds: string[], clearSelection: () => void, expenseNatureId: string | null) => {
    setBusy(true);
    commitMutation<useTxnBulkActions_AssignTxnNatureMutation>(environment, {
      mutation: assignTxnNatureMutation,
      variables: { input: { txnIds, expenseNatureId } },
      onCompleted: (res) => {
        if (res.assignTxnNature?.errors?.length) return fail(res.assignTxnNature.errors[0].message);
        after(clearSelection, `Tagged the nature on ${txnIds.length} transaction(s).`);
      },
      onError: () => fail("Could not tag nature — please try again."),
    });
  };

  const sendToNeedsReview = (txnIds: string[], clearSelection: () => void) => {
    setBusy(true);
    commitMutation<useTxnBulkActions_SetTxnsNeedsReviewMutation>(environment, {
      mutation: setTxnsNeedsReviewMutation,
      variables: { input: { txnIds, needsReview: true } },
      onCompleted: (res) => {
        if (res.setTxnsNeedsReview?.errors?.length) return fail(res.setTxnsNeedsReview.errors[0].message);
        after(clearSelection, `Sent ${txnIds.length} to the needs-review worklist.`);
      },
      onError: () => fail("Could not send to needs-review — please try again."),
    });
  };

  const renderBulkActions = (selectedIds: string[], clearSelection: () => void): ReactNode => (
    <>
      {has("setCounterparty") && (
        <BulkAction label="Set counterparty" disabled={busy}>
          {(close) => (
            <StakeholderCombobox
              options={stakeholderOptions}
              onPick={(choice) => {
                close();
                attribute(selectedIds, clearSelection, choice);
              }}
            />
          )}
        </BulkAction>
      )}
      {has("createRule") && (
        <button
          type="button"
          disabled={busy}
          className="text-sm px-2.5 py-1 rounded border border-gray-300 bg-white text-gray-900 hover:bg-gray-100 disabled:opacity-50"
          onClick={() => {
            const first = rows.find((r) => selectedIds.includes(r.txn.id));
            setRuleSeed({ description: first?.description ?? "", clearSelection });
          }}
        >
          Create rule…
        </button>
      )}
      {has("markInternal") && (
        <BulkAction label="Mark internal" disabled={busy}>
          {(close) => (
            <AccountCombobox
              options={accountOptions}
              placeholder="The owned account on the other end…"
              onPick={(choice) => {
                close();
                markInternal(selectedIds, clearSelection, choice);
              }}
            />
          )}
        </BulkAction>
      )}
      {has("reclassify") && (
        <BulkAction label="Reclassify" disabled={busy}>
          {(close) => (
            <ul className="min-w-[200px] text-sm">
              {RECLASSIFY_OPTIONS.map((option) => (
                <li key={option.label}>
                  <button
                    type="button"
                    className="w-full text-left px-2 py-1.5 rounded hover:bg-gray-100"
                    onClick={() => {
                      close();
                      reclassify(selectedIds, clearSelection, option.value);
                    }}
                  >
                    {option.label}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </BulkAction>
      )}
      {has("tagNature") && (
        <BulkAction label="Tag nature" disabled={busy}>
          {(close) => (
            <ul className="min-w-[200px] max-h-72 overflow-auto text-sm">
              <li>
                <button
                  type="button"
                  className="w-full text-left px-2 py-1.5 rounded hover:bg-gray-100 text-gray-500"
                  onClick={() => {
                    close();
                    tagNature(selectedIds, clearSelection, null);
                  }}
                >
                  Clear nature
                </button>
              </li>
              {natureOptions.map((nature) => (
                <li key={nature.id}>
                  <button
                    type="button"
                    className="w-full text-left px-2 py-1.5 rounded hover:bg-gray-100"
                    onClick={() => {
                      close();
                      tagNature(selectedIds, clearSelection, nature.id);
                    }}
                  >
                    {nature.name}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </BulkAction>
      )}
      {has("sendToNeedsReview") && (
        <button
          type="button"
          disabled={busy}
          className="text-sm px-2.5 py-1 rounded border border-gray-300 bg-white text-gray-900 hover:bg-gray-100 disabled:opacity-50"
          onClick={() => sendToNeedsReview(selectedIds, clearSelection)}
        >
          Send to needs-review
        </button>
      )}
    </>
  );

  const dialogs: ReactNode = (
    <>
      {ruleSeed && (
        <CreateRuleDialog
          description={ruleSeed.description}
          stakeholders={stakeholderOptions}
          accounts={accountOptions}
          natures={natureOptions}
          onClose={() => setRuleSeed(null)}
          onApplied={(message) => {
            ruleSeed.clearSelection();
            setRuleSeed(null);
            onChanged();
            notify({ message, kind: "success" });
          }}
        />
      )}
      {ruleConflict && (
        <RuleConflictDialog
          rules={ruleConflict.rules}
          txnIds={ruleConflict.txnIds}
          movedFromName={ruleConflict.movedFromName}
          movedToName={ruleConflict.movedToName}
          movedToId={ruleConflict.movedToId}
          perspectiveId={perspectiveId ?? null}
          onResolved={onChanged}
          onClose={() => setRuleConflict(null)}
        />
      )}
    </>
  );

  return { busy, renderBulkActions, dialogs };
}
